/**
 * Colour scheme preference: an explicit choice, or `system` to follow the OS.
 *
 * The `dark` class on `<html>` is the only thing the CSS reads, so every change
 * funnels through `apply` and the class can never drift from the stored choice.
 */
export type ThemeMode = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'bb_theme';

export function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'system' || value === 'light' || value === 'dark';
}

function darkQuery(): MediaQueryList {
  return window.matchMedia('(prefers-color-scheme: dark)');
}

function readStoredMode(): ThemeMode {
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch (err) {
    // Private browsing can throw on any storage access. Following the OS is the
    // right behaviour when no preference is readable.
    return 'system';
  }
  return isThemeMode(stored) ? stored : 'system';
}

let mode: ThemeMode = readStoredMode();
const listeners = new Set<() => void>();

function apply(): void {
  document.documentElement.classList.toggle('dark', getResolvedTheme() === 'dark');
}

function notify(): void {
  listeners.forEach((listener) => listener());
}

export function getThemeMode(): ThemeMode {
  return mode;
}

export function getResolvedTheme(): ResolvedTheme {
  if (mode !== 'system') return mode;
  return darkQuery().matches ? 'dark' : 'light';
}

export function setThemeMode(next: ThemeMode): void {
  mode = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch (err) {
    // The choice still applies for this session; only persistence is lost, so
    // this must not interrupt the change the user just asked for.
    console.warn('Could not save the theme preference.', err);
  }
  apply();
  notify();
}

export function subscribeToTheme(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Applies the stored preference before React mounts so the first paint is
 * already correct. Tracks the OS too, but only while the mode is `system`: an
 * explicit choice has to survive the OS flipping underneath it.
 */
export function initTheme(): void {
  apply();
  darkQuery().addEventListener('change', () => {
    if (mode !== 'system') return;
    apply();
    notify();
  });
}
